import { Immutable } from "immer";

import { constants } from "@reactivated";

import { LineStatus } from "../../constants";
import {
    EventState,
    FreightDeliverySteps,
    OrderDataSource,
    SmallPackageDeliverySteps,
    TRSDeliverySteps,
    UnifiedDeliveryEvent,
    UnifiedOrderLookupOrder,
    UnifiedOrderLookupOrderShipment,
    UnifiedOrderShipmentEvent,
} from "./types";

// Constants for magic strings
const DTC_FAKE_EVENT_STEPS = [
    "Pending",
    "Order Processing",
    "Order Prepared for Shipment",
] as const;

const FAKE_EVENT_FINAL_STEP = "Order Prepared for Shipment";

// Helper functions for business logic
export const isDTCFakeEvent = (trackerStep: string): boolean =>
    (DTC_FAKE_EVENT_STEPS as readonly string[]).includes(trackerStep);

export const isFinalDTCFakeEvent = (trackerStep: string): boolean =>
    trackerStep === FAKE_EVENT_FINAL_STEP;

export const findLineStatusByTrackerStep = (
    trackerStep: string,
): LineStatus | null => {
    const status = Object.values(LineStatus).find(
        /* eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison */
        (status) => status === trackerStep,
    );
    return status || null;
};

export const getClosestStep = (
    events: readonly UnifiedOrderShipmentEvent[],
    source: OrderDataSource,
): LineStatus => {
    // Find the last event with a valid status_code
    for (let i = events.length - 1; i >= 0; i--) {
        const statusKey = events[i].status_code;
        const lineStatus = LineStatus[statusKey as keyof typeof LineStatus];
        if (statusKey && lineStatus) {
            return lineStatus;
        }
    }

    // If no valid status found, fall back to default
    return source === "DTC" ? LineStatus.PREPARE_TO_SHIP : LineStatus.PENDING;
};

export const validateEventInputs = (
    order: Immutable<UnifiedOrderLookupOrder> | null,
    shipment: Immutable<UnifiedOrderLookupOrderShipment> | null,
    freightDeliverySteps:
        | Immutable<TRSDeliverySteps>
        | Immutable<FreightDeliverySteps>
        | Immutable<SmallPackageDeliverySteps>
        | null,
): boolean => {
    return !!(order && shipment && freightDeliverySteps);
};

export const findMatchingApiEvent = (
    shipment: Immutable<UnifiedOrderLookupOrderShipment>,
    trackerStep: string,
): UnifiedOrderShipmentEvent | null => {
    return (
        shipment.events.find(
            (event) =>
                LineStatus[event.status_code as keyof typeof LineStatus] ===
                trackerStep,
        ) || null
    );
};

export const getEnvironmentDurationFactor = (): number => {
    // Use NODE_ENV to determine if we're in production
    // In "master": factor = 1 (24-hour intervals)
    // In "dev"/"qa": factor = 24 (1-hour intervals)
    return (constants.CI_COMMIT_REF_SLUG as string) === "master" ? 1 : 24;
};

export const calculateDTCFakeEventState = (
    trackerStep: string,
    orderCreatedDate: string,
    currentDate: Date = new Date(),
): EventState => {
    const createdDate = new Date(orderCreatedDate);
    const factor = getEnvironmentDurationFactor();

    // Calculate hours difference, then convert to "adjusted days" using the factor
    const hoursDifference = Math.floor(
        (currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60),
    );
    const adjustedDays = Math.floor(hoursDifference / (24 / factor));

    // Determine which step should be CURRENT based on adjusted days
    let currentStep: string;
    if (adjustedDays === 0) {
        currentStep = "Pending";
    } else if (adjustedDays === 1) {
        currentStep = "Order Processing";
    } else {
        currentStep = "Order Prepared for Shipment";
    }

    // Set the state based on the current step
    if (trackerStep === currentStep) {
        return EventState.CURRENT;
    }

    // Steps before the current step are COMPLETE
    const stepOrder = [
        "Pending",
        "Order Processing",
        "Order Prepared for Shipment",
    ];
    const currentStepIndex = stepOrder.indexOf(currentStep);
    const thisStepIndex = stepOrder.indexOf(trackerStep);

    if (thisStepIndex < currentStepIndex) {
        return EventState.COMPLETE;
    } else {
        return EventState.INCOMPLETE;
    }
};

export const calculateEventState = (
    apiEvent: UnifiedOrderShipmentEvent | null,
    matchedStatus: LineStatus | null,
    closestStep: LineStatus,
): EventState => {
    if (apiEvent) {
        return EventState.COMPLETE;
    }
    if (matchedStatus === closestStep) {
        return EventState.CURRENT;
    }
    return EventState.INCOMPLETE;
};

export const getEventContent = (
    step: {
        value: {
            tracking_step_copy?: string | null;
            tracking_step_copy_condensed?: string | null;
        };
    },
    shouldShowContent: boolean,
) => {
    return {
        tracking_step_copy: shouldShowContent
            ? step.value.tracking_step_copy || null
            : null,
        tracking_step_copy_condensed: shouldShowContent
            ? step.value.tracking_step_copy_condensed || null
            : null,
    };
};

export const normalizeEventStates = <T extends UnifiedDeliveryEvent>(
    events: T[],
): T[] => {
    const lastCompleteIndex = events.findLastIndex(
        (event) =>
            event.event_state === EventState.COMPLETE ||
            event.event_state === EventState.CURRENT,
    );

    if (lastCompleteIndex !== -1) {
        for (let i = 0; i <= lastCompleteIndex; i++) {
            if (events[i].event_state === EventState.INCOMPLETE) {
                events[i].event_state = EventState.COMPLETE;
            }
        }
    }

    // Ensure there's always a CURRENT step
    const hasCurrentStep = events.some(
        (event) => event.event_state === EventState.CURRENT,
    );

    if (!hasCurrentStep && events.length > 0) {
        // Find the last COMPLETE step and make it CURRENT
        const lastCompleteStepIndex = events.findLastIndex(
            (event) => event.event_state === EventState.COMPLETE,
        );

        if (lastCompleteStepIndex !== -1) {
            events[lastCompleteStepIndex].event_state = EventState.CURRENT;
        } else {
            // If no COMPLETE steps exist, make the last step CURRENT
            events[events.length - 1].event_state = EventState.CURRENT;
        }
    }

    return events;
};

export const createTRSEvents = (
    order: Immutable<UnifiedOrderLookupOrder>,
    shipment: Immutable<UnifiedOrderLookupOrderShipment>,
    freightDeliverySteps:
        | Immutable<TRSDeliverySteps>
        | Immutable<FreightDeliverySteps>
        | Immutable<SmallPackageDeliverySteps>,
    closestStep: LineStatus,
): (UnifiedDeliveryEvent & { had_api_event: boolean })[] => {
    const events: (UnifiedDeliveryEvent & { had_api_event: boolean })[] = [];

    freightDeliverySteps.value.forEach((step) => {
        const trackerStep = step.value.tracker_step;
        if (!trackerStep) return;

        const api_event = findMatchingApiEvent(shipment, trackerStep);
        const matchedStatus = findLineStatusByTrackerStep(trackerStep);
        const eventState = calculateEventState(
            api_event,
            matchedStatus,
            closestStep,
        );

        // Make sure to exclude the DTC fake events that are a part of the CMS configuration
        if (matchedStatus !== null) {
            // For TRS PENDING events without API data, treat as if they have API event for content
            const isPendingWithoutApi =
                !api_event && matchedStatus === LineStatus.PENDING;

            // For TRS PENDING events without API data, use order creation date
            const eventDate = api_event
                ? api_event.event_date
                : matchedStatus === LineStatus.PENDING
                  ? order.created_dt
                  : null;

            // For TRS PENDING events without API data, calculate special event state
            const finalEventState = isPendingWithoutApi
                ? shipment.events.length === 0
                    ? EventState.CURRENT
                    : EventState.COMPLETE
                : eventState;

            events.push({
                status: matchedStatus,
                event_state: finalEventState,
                date: eventDate,
                tracking_step_title: step.value.tracking_step_title,
                tracking_step_copy: null, // Will be set later
                tracking_step_copy_condensed: null, // Will be set later
                icon_code: step.value.tracker_step || null,
                had_api_event: !!api_event || isPendingWithoutApi,
            });
        }
    });

    return events;
};

export const createDTCEvents = (
    order: Immutable<UnifiedOrderLookupOrder>,
    shipment: Immutable<UnifiedOrderLookupOrderShipment>,
    freightDeliverySteps:
        | Immutable<TRSDeliverySteps>
        | Immutable<FreightDeliverySteps>
        | Immutable<SmallPackageDeliverySteps>,
    closestStep: LineStatus,
): (UnifiedDeliveryEvent & { had_api_event: boolean })[] => {
    const events: (UnifiedDeliveryEvent & { had_api_event: boolean })[] = [];

    freightDeliverySteps.value.forEach((step) => {
        const trackerStep = step.value.tracker_step;
        if (!trackerStep) return;

        const api_event = findMatchingApiEvent(shipment, trackerStep);
        const matchedStatus = findLineStatusByTrackerStep(trackerStep);
        const eventState = calculateEventState(
            api_event,
            matchedStatus,
            closestStep,
        );

        if (matchedStatus !== null) {
            // Create fake events for the first 3 statuses
            if (isDTCFakeEvent(trackerStep)) {
                // Use date-based logic when no real events exist, otherwise mark as COMPLETE
                const fakeEventState =
                    shipment.events.length === 0
                        ? calculateDTCFakeEventState(
                              trackerStep,
                              order.created_dt,
                          )
                        : EventState.COMPLETE;

                events.push({
                    status: matchedStatus,
                    event_state: fakeEventState,
                    date: order.created_dt, // Always use created_dt for fake events
                    tracking_step_title: step.value.tracking_step_title,
                    tracking_step_copy: null, // Will be set later
                    tracking_step_copy_condensed: null, // Will be set later
                    icon_code: step.value.tracker_step || null,
                    had_api_event: true, // Fake events always show content
                });
            } else {
                events.push({
                    status: matchedStatus,
                    event_state: eventState,
                    date: api_event ? api_event.event_date : null,
                    tracking_step_title: step.value.tracking_step_title,
                    tracking_step_copy: null, // Will be set later
                    tracking_step_copy_condensed: null, // Will be set later
                    icon_code: step.value.tracker_step || null,
                    had_api_event: !!api_event,
                });
            }
        }
    });

    return events;
};

export const enrichEventsWithContent = (
    events: (UnifiedDeliveryEvent & { had_api_event: boolean })[],
    freightDeliverySteps:
        | Immutable<TRSDeliverySteps>
        | Immutable<FreightDeliverySteps>
        | Immutable<SmallPackageDeliverySteps>,
    withAllContent: boolean,
): UnifiedDeliveryEvent[] => {
    return events.map((event) => {
        const step = freightDeliverySteps.value.find(
            (s) => s.value.tracker_step === event.status,
        );

        if (!step) return event;

        const shouldShowContent =
            withAllContent ||
            event.had_api_event ||
            event.event_state === EventState.COMPLETE ||
            event.event_state === EventState.CURRENT;

        const content = getEventContent(step, shouldShowContent);

        // Remove the had_api_event property and return the final event
        const { had_api_event, ...finalEvent } = event;
        return {
            ...finalEvent,
            tracking_step_copy: content.tracking_step_copy,
            tracking_step_copy_condensed: content.tracking_step_copy_condensed,
        };
    });
};

export const getUnifiedEvents = (
    order: Immutable<UnifiedOrderLookupOrder> | null,
    shipment: Immutable<UnifiedOrderLookupOrderShipment> | null,
    freightDeliverySteps:
        | Immutable<TRSDeliverySteps>
        | Immutable<FreightDeliverySteps>
        | Immutable<SmallPackageDeliverySteps>
        | null,
    closestStep: LineStatus,
    withAllContent: boolean,
): UnifiedDeliveryEvent[] => {
    if (!validateEventInputs(order, shipment, freightDeliverySteps)) {
        return [];
    }

    // TypeScript now knows these are not null due to validation above
    const validOrder = order!;
    const validShipment = shipment!;
    const validSteps = freightDeliverySteps!;

    // Create basic events without content
    const basicEvents =
        validOrder.source === "TRS"
            ? createTRSEvents(
                  validOrder,
                  validShipment,
                  validSteps,
                  closestStep,
              )
            : createDTCEvents(
                  validOrder,
                  validShipment,
                  validSteps,
                  closestStep,
              );

    // Normalize states first (preserving had_api_event property)
    const normalizedEvents = normalizeEventStates(basicEvents);

    // Now enrich with content based on final states
    return enrichEventsWithContent(
        normalizedEvents,
        validSteps,
        withAllContent,
    );
};

export const formatScheduledDate = (
    scheduledDate: string | null | undefined,
): string | null => {
    if (!scheduledDate) {
        return null;
    }

    const [year, month, day] = scheduledDate.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
    });
};

export const buildOrderTitle = (closestStep: LineStatus): string => {
    if (closestStep === LineStatus.ARRIVED_AT_LOCAL_CARRIER) {
        return LineStatus.READY_DELIVERY;
    } else if (closestStep === LineStatus.SHIPPED) {
        return "Shipped to Carrier";
    }
    return closestStep;
};

export const shouldShowDeliveryDate = (closestStep: LineStatus): boolean => {
    return (
        closestStep === LineStatus.DELIVERY_SCHEDULED ||
        closestStep === LineStatus.OUT_FOR_DELIVERY ||
        closestStep === LineStatus.DELIVERED
    );
};

export const shouldShowTrackingNumber = (closestStep: LineStatus): boolean => {
    return (
        closestStep === LineStatus.SHIPPED ||
        closestStep === LineStatus.ARRIVED_AT_LOCAL_CARRIER ||
        closestStep === LineStatus.READY_DELIVERY ||
        shouldShowDeliveryDate(closestStep)
    );
};
